import React, { useCallback } from 'react';
import classNames from 'classnames';
import { Box, makeStyles, Typography } from 'housecall-ui';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import moment from 'moment';

import { SmallCardButton } from '../../components/SmallCardButton';

type StyleProps = {
  selected: boolean;
  disabled: boolean;
};

const useStyles = makeStyles((theme: HCPTheme) => ({
  card: {
    height: theme.spacing(10.5),
    width: theme.spacing(10.5),
    color: theme.palette.brandPrimary.main,
    border: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  cardSelected: {
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
  },
  cardDisabled: {
    color: theme.palette.secondary.light,
    userEvents: 'none',
    '&:hover': {
      color: theme.palette.secondary.light,
    },
  },
  button: ({ disabled }: StyleProps) => ({
    color: 'inherit',
    pointerEvents: disabled ? 'none' : 'auto',
    cursor: disabled ? 'default' : 'pointer',
    textTransform: 'none',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    minWidth: '0',
  }),
  text: {
    color: 'inherit',
  },
}));

type Props = {
  day: Date;
  selected?: boolean;
  disabled: boolean;
  onClick: (newDate: string) => void;
};

export const DayCard = ({ day, selected = false, disabled, onClick }: Props) => {
  const { card, cardSelected, cardDisabled, button, text } = useStyles({ selected, disabled });
  const clickHandle = useCallback(() => onClick(moment(day).format('YYYY-MM-DD')), [day, onClick]);

  const cardClasses = classNames(card, { [cardSelected]: selected, [cardDisabled]: disabled });

  return (
    <SmallCardButton onClick={clickHandle} classes={{ card: cardClasses, button }}>
      <Box data-testid={`day-card${selected ? 'day-card-selected' : ''}`}>
        <Typography className={text} variant={disabled ? 'caption' : 'body1'} display="block">
          {disabled ? 'Unavailable' : moment(day).format('ddd')}
        </Typography>
        <Typography className={text} variant="caption" noWrap display="block">
          {moment(day).format('MMM Do')}
        </Typography>
      </Box>
    </SmallCardButton>
  );
};
